import Uppy from '@uppy/core'
import type { UppyEventMap, UppyFile, UppyOptions } from '@uppy/core'
import XHR from '@uppy/xhr-upload'
import { useMemo } from 'react'

import { MAX_DOUBLELOOP_UPLOADER_FILE_SIZE } from '@app/lib/globals'
import GenerateUploaderTicketPlugin from '@app/shared/uploader/generateUploaderTicketPlugin'
import type { FileTicketMeta, RemoteFile } from '@app/shared/uploader/types'
import { useUploadTicketGenerateMutation } from '@graphql/queries'

const MAX_FILES = 20 // OpenAI limit

type Props = {
  onUploadSuccess?: UppyEventMap<FileTicketMeta, RemoteFile>['upload-success']
  onFileRemoved?: UppyEventMap<FileTicketMeta, RemoteFile>['file-removed']
  onComplete?: UppyEventMap<FileTicketMeta, RemoteFile>['complete']
  // dropTarget?: HTMLElement | null
  endpoint?: string | null
  extraUppyOptions?: Partial<UppyOptions<FileTicketMeta, RemoteFile>>
}

const uppyOptions: UppyOptions<FileTicketMeta, RemoteFile> = {
  autoProceed: true,
  restrictions: {
    maxFileSize: MAX_DOUBLELOOP_UPLOADER_FILE_SIZE,
    maxNumberOfFiles: MAX_FILES,
    minFileSize: 0,
    maxTotalFileSize: 0,
    minNumberOfFiles: 0,
    allowedFileTypes: [
      '.c',
      '.cpp',
      '.cs',
      '.css',
      '.doc',
      '.docx',
      '.html',
      '.java',
      '.js',
      '.json',
      '.md',
      '.pdf',
      '.php',
      '.pptx',
      '.py',
      '.rb',
      '.sh',
      '.tex',
      '.ts',
      '.txt'
    ],
    requiredMetaFields: []
  }
}

type UseOpenAiUppyInstance = (props: Props) => Uppy<FileTicketMeta, RemoteFile>

const useOpenAiUppyInstance: UseOpenAiUppyInstance = ({
  onUploadSuccess = null,
  onFileRemoved = null,
  onComplete = null,
  endpoint = null,
  extraUppyOptions = null
}) => {
  const ENDPOINT = endpoint || import.meta.env.PUBLIC_UPLOADER_ENDPOINT
  const [, getTicket] = useUploadTicketGenerateMutation()

  const uppyMemo = useMemo(() => {
    const mergedUppyOptions = { ...uppyOptions, ...(extraUppyOptions || {}) }
    const uppy = new Uppy<FileTicketMeta, RemoteFile>(mergedUppyOptions)
    uppy.use(GenerateUploaderTicketPlugin, { getTicket })

    uppy.use(XHR, {
      endpoint: ENDPOINT,
      formData: false,
      headers: (file: UppyFile<FileTicketMeta, RemoteFile>) => ({
        'Content-Type': file.type,
        'X-Filename': encodeURIComponent(file.name),
        'X-Ticket': file.meta.ticket
      })
    })

    if (onUploadSuccess) {
      uppy.on('upload-success', onUploadSuccess)
    }

    if (onFileRemoved) {
      uppy.on('file-removed', onFileRemoved)
    }

    if (onComplete) {
      uppy.on('complete', onComplete)
    }

    return uppy
  }, [extraUppyOptions, getTicket, ENDPOINT, onUploadSuccess, onFileRemoved, onComplete])

  return uppyMemo
}

export default useOpenAiUppyInstance
